import {useEffect, useRef} from "react";
import {addToast} from "@heroui/toast";
import services from "@/services";

/**
 * Handles the return from an Event Pass Stripe Checkout. When the URL carries
 * `?event_pass=success&session_id=…` (set as the checkout success_url), this:
 *   1. confirms the purchase so the pass is created immediately (without waiting on the webhook),
 *   2. applies it to the given event so it's unlocked in one step (idempotent — safe if the webhook
 *      already fulfilled it, or the pass was already applied),
 *   3. cleans the query params off the URL and fires `onUnlocked` so the page can refetch.
 *
 * Runs once. Pass the event the purchase was started for (tournament OR league); pass `null` while
 * the uid is still loading and it will no-op until a real key is provided.
 */
export function useEventPassReturn(
    eventKey: {tournament_uid?: string; league_uid?: string} | null,
    onUnlocked?: () => void,
): void {
    const ranRef = useRef(false);
    const uid = eventKey?.tournament_uid ?? eventKey?.league_uid ?? '';

    useEffect(() => {
        if (ranRef.current || !uid) return;

        const params = new URLSearchParams(window.location.search);
        if (params.get('event_pass') !== 'success') return;

        ranRef.current = true;
        const sessionId = params.get('session_id');

        const cleanUrl = () => {
            params.delete('event_pass');
            params.delete('session_id');
            const q = params.toString();
            window.history.replaceState({}, '', window.location.pathname + (q ? `?${q}` : ''));
        };

        (async () => {
            try {
                if (sessionId) {
                    await services.eventPass.confirm(sessionId);
                }
                await services.eventPass.apply(eventKey!);
                addToast({color: 'success', title: 'Event Pass applied — this event is now unlimited.'});
            } catch {
                // Confirm/apply can legitimately no-op (webhook already fulfilled, pass already applied).
            } finally {
                cleanUrl();
                // Always resume — callers re-verify the pass themselves, so a failed apply must not
                // strand the page on its "confirming…" screen.
                onUnlocked?.();
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uid]);
}
